import { getToken } from '@contexts/Token.context';
import { fileApiUrl } from '@utils/api';
import TileState from 'ol/TileState';
import { Tile as TileLayer } from 'ol/layer';
import XYZ from 'ol/source/XYZ';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

/**
 * Loads a tile image with retry mechanism
 * @param {import('ol/ImageTile').default} imageTile - The OpenLayers image tile object
 * @param {string} src - The source URL for the tile
 * @returns {Promise<void>}
 */
const loadTile = async (imageTile, src) => {
	const img = imageTile.getImage();
	if (!src.includes(fileApiUrl)) return (img.src = src);

	let attempts = 0;
	let lastError;

	while (attempts < MAX_RETRIES) {
		try {
			const token = getToken();
			const response = await fetch(src, {
				method: 'GET',
				headers: { authorization: `Bearer ${token}` },
			});

			if (response.status === 200) {
				const { url } = await response.json();
				return (img.src = url);
			}

			if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
			if (response.status === 204) return imageTile.setState(TileState.EMPTY);
		} catch (error) {
			lastError = error;
			attempts++;

			if (attempts >= MAX_RETRIES)
				return console.error('Failed to load tile after all retries:', lastError);

			console.warn(`Tile load attempt ${attempts} failed, retrying...`);
			await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * attempts));
		}
	}
};

// FIX: tileLoadFunction is deprecated.
// https://openlayers.org/en/latest/apidoc/module-ol_source_XYZ-XYZ.html
export const makePhotoLayer = ({
	name,
	zIndex,
	defaultProjection,
	url,
	extent,
	minZoomLevel = 10,
	maxZoomLevel = 22,
	visible,
	layerId,
}) => {
	return new TileLayer({
		preload: Infinity,
		name: name,
		defaultProjection: defaultProjection,
		extent: extent,
		visible: visible,
		zIndex: zIndex,
		properties: {
			customLayerId: layerId,
		},
		source: new XYZ({
			tileLoadFunction: loadTile,
			url: url,
			tileSize: [256, 256],
			title: 'ortho',
			attributions: '',
			minZoom: minZoomLevel,
			maxZoom: maxZoomLevel,
			crossOrigin: 'anonymous',
		}),
	});
};
